import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import {
    ArrowLeft,
    ArrowRight,
    MapPin,
    Briefcase,
    Clock,
    ShieldCheck,
    Upload,
    CheckCircle2,
    Paperclip,
    Sparkles,
} from "lucide-react";
import { careersApi } from "@/lib/careersApi";
import { COUNTRIES, RIGHT_TO_WORK_OPTIONS } from "@/lib/countries";
import ShareMenu from "@/components/careers/ShareMenu";
import PageSEO, { stripMd, mdToSimpleHtml, parseSalary } from "@/components/seo/PageSEO";
import { COMPANY } from "@/lib/company";

const MAX_FILE_MB = 5;
const ACCEPTED_TYPES = ["application/pdf", "image/jpeg", "image/png"];

function formatDeadline(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

const emptyForm = {
    full_name: "",
    email: "",
    phone: "",
    nationality: "",
    current_country: "",
    right_to_work: "",
    years_experience: "",
    cover_letter: "",
};

function stripMarkdown(md, limit) {
    if (!md) return "";
    const plain = md
        .replace(/```[\s\S]*?```/g, " ")
        .replace(/`([^`]+)`/g, "$1")
        .replace(/!\[[^\]]*\]\([^)]+\)/g, " ")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/[#>*_~-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    if (!limit || plain.length <= limit) return plain;
    return plain.slice(0, limit - 1).trimEnd() + "…";
}

export default function CareerDetailPage() {
    const { slug } = useParams();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [files, setFiles] = useState({ cv: null, e_visa: null, passport: null });
    const [fileErrors, setFileErrors] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null);
    const formRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        careersApi
            .getJob(slug)
            .then((d) => {
                if (!cancelled) setJob(d);
            })
            .catch((err) => {
                if (!cancelled) {
                    if (err?.response?.status === 404) setNotFound(true);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    const setField = (name, val) => setForm((s) => ({ ...s, [name]: val }));

    const handleFile = (docType, file) => {
        setFileErrors((errs) => ({ ...errs, [docType]: null }));
        if (!file) {
            setFiles((f) => ({ ...f, [docType]: null }));
            return;
        }
        if (file.size > MAX_FILE_MB * 1024 * 1024) {
            setFileErrors((errs) => ({
                ...errs,
                [docType]: `File exceeds ${MAX_FILE_MB} MB limit`,
            }));
            return;
        }
        if (!ACCEPTED_TYPES.includes(file.type)) {
            setFileErrors((errs) => ({
                ...errs,
                [docType]: "Only PDF, JPG or PNG is accepted",
            }));
            return;
        }
        setFiles((f) => ({ ...f, [docType]: file }));
    };

    const validateForm = () => {
        const errs = {};
        if (form.full_name.trim().length < 2) errs.full_name = "Full name is required";
        if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) errs.email = "Enter a valid email address";
        if (form.phone.trim().length < 5) errs.phone = "Enter your phone number in international format";
        if (!form.nationality) errs.nationality = "Select your nationality";
        if (!form.current_country) errs.current_country = "Select where you currently live";
        if (!form.right_to_work) errs.right_to_work = "Select the option that applies to you";
        if (
            form.years_experience === "" ||
            Number.isNaN(Number(form.years_experience)) ||
            Number(form.years_experience) < 0 ||
            Number(form.years_experience) > 50
        )
            errs.years_experience = "Enter a number between 0 and 50";
        if (form.cover_letter.trim().length < 20)
            errs.cover_letter = "Your cover letter must be at least 20 characters";
        if (form.cover_letter.trim().length > 2000)
            errs.cover_letter = "Cover letter is limited to 2000 characters";
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const validateFiles = () => {
        const errs = {};
        if (!files.cv) errs.cv = "CV is required";
        if (!files.e_visa) errs.e_visa = "e-Visa document is required";
        if (!files.passport) errs.passport = "Passport is required";
        setFileErrors((prev) => ({ ...prev, ...errs }));
        return !errs.cv && !errs.e_visa && !errs.passport;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const ok = validateForm();
        const okFiles = validateFiles();
        if (!ok || !okFiles) {
            toast.error("Please fix the highlighted fields before submitting.");
            return;
        }
        setSubmitting(true);
        const fd = new FormData();
        fd.append("job_id", job.id);
        Object.entries(form).forEach(([k, v]) => fd.append(k, v.toString().trim()));
        fd.append("cv", files.cv);
        fd.append("e_visa", files.e_visa);
        fd.append("passport", files.passport);
        try {
            const res = await careersApi.submitApplication(fd);
            setSubmitted(res);
            toast.success("Application submitted", {
                description: `Reference: ${res.reference_number}`,
            });
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg =
                typeof detail === "string"
                    ? detail
                    : Array.isArray(detail)
                        ? detail.map((d) => d.msg || JSON.stringify(d)).join(" · ")
                        : "Submission failed. Please try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const scrollToApply = () => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    if (loading) {
        return (
            <div className="container-x py-24" data-testid="career-detail-loading">
                <div className="h-64 animate-pulse bg-white/60" />
            </div>
        );
    }

    if (notFound || !job) {
        return (
            <div
                className="container-x py-24 text-center"
                data-testid="career-detail-not-found"
            >
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-gold">
                    404
                </p>
                <h1 className="mt-2 font-display text-5xl uppercase tracking-wide">
                    This role is no longer open
                </h1>
                <Link
                    to="/careers"
                    className="mt-8 inline-flex items-center gap-2 border border-brand-charcoal px-6 py-3 font-mono text-xs uppercase tracking-[0.25em] hover:bg-brand-charcoal hover:text-brand-offwhite"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to careers
                </Link>
            </div>
        );
    }

    // Build JobPosting JSON-LD dynamically. Google for Jobs discovery is the
    // whole point of this page — get every field it recognises correct.
    const salary = parseSalary(job.salary_range);
    const descHtmlBase = mdToSimpleHtml(job.description_md || "");
    const sponsorshipNote = job.sponsorship_available
        ? "<p><strong>Visa sponsorship:</strong> UK Home Office licensed sponsor — Skilled Worker route available.</p>"
        : "";
    const jobPostingLd = {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        title: job.title,
        description: `${sponsorshipNote}${descHtmlBase}`,
        datePosted: job.created_at || undefined,
        validThrough: job.deadline || undefined,
        employmentType: (job.employment_type || "Full-time")
            .toUpperCase()
            .replace(/[- ]/g, "_"),
        hiringOrganization: {
            "@type": "Organization",
            name: "EB Construction",
            sameAs: "https://elevatebuildconstruction.co.uk",
            logo: "https://elevatebuildconstruction.co.uk/brand/logo-primary.png?v=3",
        },
        jobLocation: {
            "@type": "Place",
            address: {
                "@type": "PostalAddress",
                streetAddress: COMPANY.address.streetAddress,
                addressLocality: COMPANY.address.city,
                addressRegion: COMPANY.address.region,
                postalCode: COMPANY.address.postcode,
                addressCountry: COMPANY.address.country,
            },
        },
        identifier: {
            "@type": "PropertyValue",
            name: "EB Construction",
            value: job.slug,
        },
        directApply: true,
        applicantLocationRequirements: [{ "@type": "Country", name: "GB" }],
        ...(salary
            ? {
                  baseSalary: {
                      "@type": "MonetaryAmount",
                      currency: "GBP",
                      value: {
                          "@type": "QuantitativeValue",
                          value: salary.value,
                          unitText: salary.unit,
                      },
                  },
              }
            : {}),
    };

    return (
        <div data-testid="career-detail-page" className="pb-24">
            <PageSEO
                title={`${job.title} in ${job.location}${
                    salary ? ` — £${salary.value.toLocaleString()}/year` : ""
                } | EB Construction Careers`}
                description={stripMd(job.description_md, 155)}
                canonicalPath={`/careers/${job.slug}`}
                ogType="article"
                jsonLd={jobPostingLd}
            />
            <div className="bg-brand-charcoal text-brand-offwhite">
                <div className="container-x py-16 md:py-20">
                    <Link
                        to="/careers"
                        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/70 hover:text-brand-gold"
                        data-testid="career-detail-back"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        All open roles
                    </Link>
                    <div className="mt-6 flex flex-wrap items-center gap-2">
                        {job.sponsorship_available && (
                            <span
                                className="border border-brand-gold px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-gold"
                                data-testid="career-detail-sponsor-pill"
                            >
                                Sponsor
                            </span>
                        )}
                        <span className="border border-white/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/80">
                            {job.department}
                        </span>
                    </div>
                    <h1
                        className="mt-4 font-display text-5xl font-semibold leading-[1.05] md:text-6xl lg:text-7xl"
                        data-testid="career-detail-title"
                    >
                        {job.title}
                    </h1>
                    <ul className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-body text-sm text-brand-offwhite/85">
                        <li className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-brand-gold" />
                            {job.location}
                        </li>
                        <li className="flex items-center gap-2">
                            <Briefcase className="h-4 w-4 text-brand-gold" />
                            {job.employment_type}
                            {job.salary_range ? ` · ${job.salary_range}` : ""}
                        </li>
                        <li className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-brand-gold" />
                            Apply by {formatDeadline(job.deadline)}
                        </li>
                    </ul>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={scrollToApply}
                            className="inline-flex items-center gap-2 bg-brand-gold px-6 py-3 font-mono text-xs uppercase tracking-[0.25em] text-brand-charcoal transition-transform hover:-translate-y-0.5"
                            data-testid="career-detail-cta"
                        >
                            Apply for this role
                            <ArrowRight className="h-4 w-4" />
                        </button>
                        <ShareMenu
                            job={job}
                            url={typeof window !== "undefined" ? window.location.href : ""}
                            variant="button"
                            testIdPrefix="job-share-button"
                            menuTestId="job-share-menu"
                            anchor="left"
                        />
                    </div>
                </div>
            </div>

            <div className="container-x grid gap-10 pt-12 md:grid-cols-3">
                {/* Description */}
                <article className="md:col-span-2">
                    <div
                        className="prose prose-neutral max-w-none prose-headings:font-display prose-headings:uppercase prose-headings:tracking-wide prose-headings:text-brand-charcoal prose-a:text-brand-gold"
                        data-testid="career-detail-description"
                    >
                        <ReactMarkdown>{job.description_md}</ReactMarkdown>
                    </div>

                    <section className="mt-10" data-testid="career-detail-requirements">
                        <h3 className="font-display text-2xl uppercase tracking-wide">
                            Requirements
                        </h3>
                        <ul className="mt-4 space-y-2">
                            {job.requirements.map((r, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-3 border-l-2 border-brand-gold/60 pl-4"
                                >
                                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-gold" />
                                    <span className="font-body text-sm text-brand-charcoal">
                                        {r}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </section>

                    {job.benefits && job.benefits.length > 0 && (
                        <section className="mt-10" data-testid="career-detail-benefits">
                            <h3 className="font-display text-2xl uppercase tracking-wide">
                                What we offer
                            </h3>
                            <ul className="mt-4 space-y-2">
                                {job.benefits.map((b, i) => (
                                    <li
                                        key={i}
                                        className="flex items-start gap-3 border-l-2 border-brand-charcoal pl-4"
                                    >
                                        <Sparkles className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-charcoal" />
                                        <span className="font-body text-sm text-brand-charcoal">
                                            {b}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}
                </article>

                {/* Sidebar with sponsorship info */}
                <aside className="hidden md:block">
                    <div className="sticky top-24 border-l-2 border-brand-gold bg-white p-6">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-gold">
                            Documents required
                        </p>
                        <ul className="mt-4 space-y-2 font-body text-sm text-brand-charcoal">
                            <li className="flex items-center gap-2">
                                <Paperclip className="h-3.5 w-3.5 text-brand-gold" />
                                CV (PDF, JPG or PNG · ≤ 5 MB)
                            </li>
                            <li className="flex items-center gap-2">
                                <Paperclip className="h-3.5 w-3.5 text-brand-gold" />
                                e-Visa or right-to-work
                            </li>
                            <li className="flex items-center gap-2">
                                <Paperclip className="h-3.5 w-3.5 text-brand-gold" />
                                Passport bio page
                            </li>
                        </ul>
                        {job.sponsorship_available && (
                            <div className="mt-6 flex gap-2 border-t border-brand-line pt-6 text-sm text-brand-steel">
                                <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-gold" />
                                <p>
                                    This role is eligible for{" "}
                                    <span className="font-semibold text-brand-charcoal">
                                        Skilled Worker sponsorship
                                    </span>{" "}
                                    under our UK Home Office licence.
                                </p>
                            </div>
                        )}
                    </div>
                </aside>
            </div>

            {/* Application form OR success screen */}
            <div ref={formRef} className="container-x mt-16 scroll-mt-24">
                {submitted ? (
                    <SuccessScreen submitted={submitted} />
                ) : (
                    <ApplicationForm
                        job={job}
                        form={form}
                        setField={setField}
                        fieldErrors={fieldErrors}
                        files={files}
                        handleFile={handleFile}
                        fileErrors={fileErrors}
                        submitting={submitting}
                        onSubmit={handleSubmit}
                    />
                )}
            </div>
        </div>
    );
}

function ApplicationForm({
    job,
    form,
    setField,
    fieldErrors,
    files,
    handleFile,
    fileErrors,
    submitting,
    onSubmit,
}) {
    return (
        <div className="border border-brand-line bg-white p-8 md:p-12">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-gold">
                Apply · {job.title}
            </p>
            <h2 className="mt-2 font-display text-4xl uppercase tracking-wide text-brand-charcoal md:text-5xl">
                Your application
            </h2>
            <p className="mt-3 max-w-2xl font-body text-sm text-brand-steel">
                All fields marked with an asterisk are required. We aim to respond to every
                application within five working days.
            </p>

            <form
                onSubmit={onSubmit}
                className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2"
                data-testid="career-application-form"
                encType="multipart/form-data"
            >
                <PublicField
                    label="Full name*"
                    testId="app-field-full-name"
                    value={form.full_name}
                    onChange={(v) => setField("full_name", v)}
                    error={fieldErrors.full_name}
                    autoComplete="name"
                />
                <PublicField
                    label="Email*"
                    testId="app-field-email"
                    type="email"
                    value={form.email}
                    onChange={(v) => setField("email", v)}
                    error={fieldErrors.email}
                    autoComplete="email"
                />
                <PublicField
                    label="Phone (international format)*"
                    testId="app-field-phone"
                    value={form.phone}
                    onChange={(v) => setField("phone", v)}
                    placeholder="+44 …"
                    error={fieldErrors.phone}
                    autoComplete="tel"
                />
                <PublicField
                    label="Years of relevant experience*"
                    testId="app-field-years"
                    type="number"
                    min={0}
                    max={50}
                    value={form.years_experience}
                    onChange={(v) => setField("years_experience", v)}
                    error={fieldErrors.years_experience}
                />
                <PublicSelect
                    label="Nationality*"
                    testId="app-field-nationality"
                    value={form.nationality}
                    onChange={(v) => setField("nationality", v)}
                    options={COUNTRIES}
                    error={fieldErrors.nationality}
                />
                <PublicSelect
                    label="Country of current residence*"
                    testId="app-field-current-country"
                    value={form.current_country}
                    onChange={(v) => setField("current_country", v)}
                    options={COUNTRIES}
                    error={fieldErrors.current_country}
                />
                <div className="md:col-span-2">
                    <PublicSelect
                        label="UK right to work*"
                        testId="app-field-right-to-work"
                        value={form.right_to_work}
                        onChange={(v) => setField("right_to_work", v)}
                        options={RIGHT_TO_WORK_OPTIONS}
                        error={fieldErrors.right_to_work}
                    />
                </div>
                <div className="md:col-span-2">
                    <PublicTextArea
                        label={`Cover letter* (${form.cover_letter.length}/2000)`}
                        testId="app-field-cover-letter"
                        rows={8}
                        value={form.cover_letter}
                        onChange={(v) => setField("cover_letter", v.slice(0, 2000))}
                        placeholder="Tell us about your experience, why you want to join ElevateBuild, and anything else that supports your application."
                        error={fieldErrors.cover_letter}
                    />
                </div>

                {/* Documents */}
                <div className="md:col-span-2">
                    <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-steel">
                        Required documents (PDF, JPG or PNG · max 5 MB each)
                    </p>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <FileField
                            label="CV"
                            docType="cv"
                            file={files.cv}
                            onFile={(f) => handleFile("cv", f)}
                            error={fileErrors.cv}
                        />
                        <FileField
                            label="e-Visa / right-to-work"
                            docType="e_visa"
                            file={files.e_visa}
                            onFile={(f) => handleFile("e_visa", f)}
                            error={fileErrors.e_visa}
                        />
                        <FileField
                            label="Passport"
                            docType="passport"
                            file={files.passport}
                            onFile={(f) => handleFile("passport", f)}
                            error={fileErrors.passport}
                        />
                    </div>
                </div>

                <div className="md:col-span-2 flex flex-col items-start justify-between gap-4 border-t border-brand-line pt-8 md:flex-row md:items-center">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-steel">
                        By submitting you consent to ElevateBuild processing this data for
                        recruitment under UK GDPR.
                    </p>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center gap-2 bg-brand-gold px-8 py-3.5 font-mono text-xs uppercase tracking-[0.3em] text-brand-charcoal transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                        data-testid="career-application-submit"
                    >
                        {submitting ? "Submitting…" : "Submit application"}
                        <ArrowRight className="h-4 w-4" />
                    </button>
                </div>
            </form>
        </div>
    );
}

function SuccessScreen({ submitted }) {
    return (
        <div
            className="border border-brand-gold bg-white p-10 text-center md:p-14"
            data-testid="career-application-success"
        >
            <CheckCircle2 className="mx-auto h-12 w-12 text-brand-gold" />
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-brand-gold">
                Application received
            </p>
            <h2 className="mt-2 font-display text-4xl uppercase tracking-wide text-brand-charcoal md:text-5xl">
                Thanks — we've got it.
            </h2>
            <p className="mt-4 font-body text-brand-charcoal">
                Your reference number is{" "}
                <span
                    className="font-mono font-semibold text-brand-gold"
                    data-testid="career-application-reference"
                >
                    {submitted.reference_number}
                </span>
                . We'll be in touch within five working days.
            </p>
            <Link
                to="/careers"
                className="mt-8 inline-flex items-center gap-2 border border-brand-charcoal px-6 py-3 font-mono text-xs uppercase tracking-[0.25em] text-brand-charcoal hover:bg-brand-charcoal hover:text-brand-offwhite"
            >
                <ArrowLeft className="h-4 w-4" />
                See other open roles
            </Link>
        </div>
    );
}

function PublicField({
    label,
    value,
    onChange,
    error,
    testId,
    type = "text",
    ...rest
}) {
    return (
        <label className="block">
            <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-brand-steel">
                {label}
            </span>
            <input
                {...rest}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={`w-full border-b bg-transparent py-3 font-body text-base text-brand-charcoal placeholder:text-brand-steel/50 focus:border-brand-gold focus:outline-none ${
                    error ? "border-rose-500" : "border-brand-steel/40"
                }`}
                data-testid={testId}
            />
            {error && (
                <span
                    className="mt-1 block font-mono text-[10px] text-rose-600"
                    data-testid={`${testId}-error`}
                >
                    {error}
                </span>
            )}
        </label>
    );
}

function PublicSelect({ label, value, onChange, options, error, testId }) {
    return (
        <label className="block">
            <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-brand-steel">
                {label}
            </span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className={`w-full border-b bg-transparent py-3 font-body text-base text-brand-charcoal focus:border-brand-gold focus:outline-none ${
                    error ? "border-rose-500" : "border-brand-steel/40"
                }`}
                data-testid={testId}
            >
                <option value="">Please select…</option>
                {options.map((o) => (
                    <option key={o} value={o}>
                        {o}
                    </option>
                ))}
            </select>
            {error && (
                <span
                    className="mt-1 block font-mono text-[10px] text-rose-600"
                    data-testid={`${testId}-error`}
                >
                    {error}
                </span>
            )}
        </label>
    );
}

function PublicTextArea({ label, value, onChange, error, rows, testId, ...rest }) {
    return (
        <label className="block">
            <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-brand-steel">
                {label}
            </span>
            <textarea
                rows={rows || 6}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                {...rest}
                className={`w-full border bg-white/50 p-3 font-body text-sm leading-relaxed text-brand-charcoal placeholder:text-brand-steel/50 focus:border-brand-gold focus:outline-none ${
                    error ? "border-rose-500" : "border-brand-steel/30"
                }`}
                data-testid={testId}
            />
            {error && (
                <span
                    className="mt-1 block font-mono text-[10px] text-rose-600"
                    data-testid={`${testId}-error`}
                >
                    {error}
                </span>
            )}
        </label>
    );
}

function FileField({ label, docType, file, onFile, error }) {
    const inputId = `career-file-${docType}`;
    return (
        <div className="flex flex-col">
            <label
                htmlFor={inputId}
                className={`group flex cursor-pointer flex-col items-start justify-center gap-2 border-2 border-dashed p-4 transition-colors ${
                    error
                        ? "border-rose-500 bg-rose-50/40"
                        : file
                            ? "border-brand-gold bg-brand-gold/5"
                            : "border-brand-line bg-white hover:border-brand-gold"
                }`}
                data-testid={`career-file-label-${docType}`}
            >
                <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-steel">
                    <Upload className="h-3.5 w-3.5" />
                    {label}
                </span>
                {file ? (
                    <p className="max-w-full break-words font-body text-sm text-brand-charcoal">
                        {file.name}
                    </p>
                ) : (
                    <p className="font-body text-xs text-brand-steel">
                        Drop or click to select
                    </p>
                )}
                <input
                    id={inputId}
                    type="file"
                    accept="application/pdf,image/jpeg,image/png"
                    className="hidden"
                    onChange={(e) => onFile(e.target.files?.[0] || null)}
                    data-testid={`career-file-input-${docType}`}
                />
            </label>
            {error && (
                <span
                    className="mt-1 font-mono text-[10px] text-rose-600"
                    data-testid={`career-file-error-${docType}`}
                >
                    {error}
                </span>
            )}
        </div>
    );
}
